import { useEffect } from 'react';
import { Page } from 'layouts/Admin/Page';
import { loadScript } from 'utils/load-script';
import { useGetProject } from 'api/useProjectsApi';
import { useParams } from 'react-router';
import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import { EPermission } from 'api/core';
import { toast } from 'react-toastify';
import { useAuth } from 'auth/AuthProvider';

export const ProjectEstateOrbitPreview = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: project } = useGetProject(id);

  useEffect(() => {
    const script = loadScript(
      `${process.env.REACT_APP_MEDIA_ORBIT_MODULE_URL}/injection.js`
    );

    script.onload = () => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const existingElement = document.getElementById('estate-orbit');
            if (existingElement) {
              const projectId =
                existingElement.getAttribute('data-project-id') || '';
              const estateOrbitElement = document.createElement(
                'estate-orbit-element'
              );
              estateOrbitElement.setAttribute('data-project-id', projectId);
              existingElement.replaceWith(estateOrbitElement);
            }
          }
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });

      // Manually trigger DOMContentLoaded event
      document.dispatchEvent(
        new Event('DOMContentLoaded', {
          bubbles: true,
          cancelable: true,
        })
      );
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const demoLink = `${window.location.origin}/demo/estate-orbit/${id}`;

  if (!project) return null;

  const getDemoLink = () => {
    navigator.clipboard.writeText(demoLink);
    toast.success('Link kopieret til udklipsholderen', {
      toastId: 'copy-link',
    });
  };

  const getEmailLink = () => {
    if (!user) return;

    const subject = `Boligvælger demo: ${project.name}`;
    const newLine = '%0D%0A';

    const body = `Hej${newLine}${newLine}Boligvælger demo til ${project.name} kan ses her: ${demoLink}${newLine}${newLine}Mvh${newLine}${user.name}`;
    return `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Page
      title={project.name}
      breadcrumbs={[
        { name: 'Projekter' },
        { name: project.name ?? '' },
        { name: 'Boligvælger' },
        { name: 'Forhåndsvisning' },
      ]}
    >
      <div className="flex flex-col mt-2">
        <div className="dropdown dropdown-hover">
          <div
            data-testid="media-sequence-share"
            tabIndex={0}
            role="button"
            className="btn btn-primary"
          >
            Del boligvælger demo
          </div>
          <ul
            tabIndex={0}
            className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52"
          >
            <li>
              <a onClick={getDemoLink}>Kopier link</a>
              <a href={getEmailLink()} target="_blank" rel="noreferrer">
                Del via email
              </a>
            </li>
          </ul>
        </div>

        <div className="pt-4">
          <div id="estate-orbit" data-project-id={id} />
        </div>

        <PermissionProtectedComponent
          permissions={[
            EPermission.ModuleBoligvaelger,
            EPermission.EstateOrbitViewInjectionCode,
          ]}
        >
          <div className="mt-4 space-y-4">
            <h2 className="text-xl font-semibold">Injektionskode</h2>
            <div className="mockup-code text-primary">
              <pre className="px-6 select-all">
                <code>
                  {`
<div id="estate-orbit" data-project-id="${id}"></div>
<script src="${process.env.REACT_APP_MEDIA_ORBIT_MODULE_URL}/injection.js"></script>
    `.trim()}
                </code>
              </pre>
            </div>
          </div>
        </PermissionProtectedComponent>
      </div>
    </Page>
  );
};
